import { useQuery } from '@tanstack/react-query'
import { pctTexto, ROTULO_REGIME_CONTRAPARTE, TRATAMENTOS, type EntradaCalculadora, type ModoCalculadora, type PontoCalculadora, type RegimeContraparte } from '@via-tax/contracts'
import { Aviso, Selo } from '@via-tax/ui'
import { useEffect, useState } from 'react'
import { EntradaNumero } from '../components/EntradaNumero'
import { reais } from '../formatos'
import type { ContextoCalculadora, SaidaCalculadora } from '../tipos'
import { mensagemDeErro, useTRPC } from '../trpc'

/**
 * Calculadora de formação de preço (ADR 0030): uma venda por vez, para abrir na frente do cliente.
 *
 * Dois modos. "Já vendo por" converte um preço atual; "Formar do zero" parte do custo e da margem.
 * Nos dois, o DAS sai da faixa real da empresa, o IBS/CBS entra por fora no fim, e o crédito das
 * compras ligadas àquela venda abate o caixa. A conta é do motor — a tela só mostra.
 */

const ROTULO_TRATAMENTO: Partial<Record<(typeof TRATAMENTOS)[number], string>> = {
  INTEGRAL: 'Integral (sem redução)',
  REDUCAO_30: 'Redução de 30% (profissão intelectual)',
  REDUCAO_60: 'Redução de 60% (saúde, educação, alimentos…)',
  ISENCAO: 'Isento',
  ALIQUOTA_ZERO: 'Alíquota zero (cesta básica)',
  MONOFASICO: 'Monofásico (combustível)',
  DIFERIMENTO: 'Diferimento',
}
const REGIMES_UTEIS: RegimeContraparte[] = ['REGULAR', 'SN_HIBRIDO', 'SN_PURO', 'MEI', 'PF_NAO_CONTRIBUINTE']

export function Calculadora() {
  const trpc = useTRPC()
  const [modo, setModo] = useState<ModoCalculadora>('CONVERTER')
  const [e, setE] = useState<EntradaCalculadora>({
    ano: 2027,
    modo: 'CONVERTER',
    valorVenda: 1_000_000,
    custo: 600_000,
    despesas: 30_000,
    margem: 200_000,
    compra: 0,
    regimeFornecedor: 'REGULAR',
    clienteCredita: true,
    tratamento: 'INTEGRAL',
    creditoDesejado: 0,
    regimeEmpresa: 'SIMPLES',
    pisCofins: 36_500,
    icms: 0,
    iss: 0,
    ipi: 0,
    icmsCompra: 0,
    clienteCreditaPisCofins: false,
  })
  const contexto = useQuery(trpc.calculadora.contexto.queryOptions())
  // A calculadora abre no regime da empresa; trocar o seletor permite simular "e se fosse outro".
  // O regime é DERIVADO enquanto ninguém tocou no seletor — sincronizar por efeito dispararia render extra.
  const [regimeTocado, setRegimeTocado] = useState(false)
  const regime = regimeTocado ? e.regimeEmpresa : (contexto.data?.regimeCalculadora ?? e.regimeEmpresa)
  const pisCofinsPadrao = regime === 'REAL' ? 92_500 : 36_500
  const entrada: EntradaCalculadora = regimeTocado ? e : { ...e, regimeEmpresa: regime, pisCofins: pisCofinsPadrao }

  // Sem isto a conta ia ao servidor a cada tecla; 350 ms deixa a digitação fluida.
  const [pedido, setPedido] = useState(e)
  useEffect(() => {
    const t = setTimeout(() => setPedido({ ...e, modo, regimeEmpresa: regime, pisCofins: regimeTocado ? e.pisCofins : pisCofinsPadrao }), 350)
    return () => clearTimeout(t)
  }, [e, modo, regime, regimeTocado, pisCofinsPadrao])
  const q = useQuery(trpc.calculadora.preco.queryOptions(pedido))
  const trocarRegime = (regime: EntradaCalculadora['regimeEmpresa']) => {
    setRegimeTocado(true)
    setE((x) => ({ ...x, regimeEmpresa: regime, pisCofins: regime === 'REAL' ? 92_500 : 36_500 }))
  }
  return <PainelCalculadora e={entrada} setE={setE} modo={modo} setModo={setModo} contexto={contexto.data} r={q.data} erro={q.isError ? q.error : null} trocarRegime={trocarRegime} />
}

/** Só a aparência: recebe os dados prontos, para a tela e para a conferência visual. */
export function PainelCalculadora({
  e,
  setE,
  modo,
  setModo,
  contexto,
  r,
  erro,
  trocarRegime,
}: {
  e: EntradaCalculadora
  setE: (f: (x: EntradaCalculadora) => EntradaCalculadora) => void
  modo: ModoCalculadora
  setModo: (m: ModoCalculadora) => void
  contexto: ContextoCalculadora | undefined
  r: SaidaCalculadora | undefined
  erro: unknown
  trocarRegime: (regime: EntradaCalculadora['regimeEmpresa']) => void
}) {
  const campo = <K extends keyof EntradaCalculadora>(chave: K, valor: EntradaCalculadora[K]) => setE((x) => ({ ...x, [chave]: valor }))
  const converter = modo === 'CONVERTER'
  const simples = e.regimeEmpresa === 'SIMPLES'

  const colunas: Array<{ titulo: string; nota: string; p: PontoCalculadora; destaque?: boolean }> = r
    ? converter
      ? [
          { titulo: 'Hoje', nota: { SIMPLES: 'Simples como está', PRESUMIDO: 'Lucro Presumido', REAL: 'Lucro Real' }[e.regimeEmpresa], p: r.hoje },
          { titulo: 'Mesma nota', nota: 'cliente paga o mesmo total', p: r.mesmaNota },
          { titulo: 'Piso', nota: 'mantém o seu caixa', p: r.piso, destaque: true },
          { titulo: 'Meio', nota: 'divide o ganho', p: r.meio },
          { titulo: 'Teto', nota: 'custo do cliente igual', p: r.teto },
        ]
      : [
          { titulo: 'Hoje', nota: { SIMPLES: 'Simples como está', PRESUMIDO: 'Lucro Presumido', REAL: 'Lucro Real' }[e.regimeEmpresa], p: r.hoje },
          { titulo: simples ? 'No híbrido' : `Em ${r.ano}`, nota: 'mesma margem, com o crédito', p: r.piso, destaque: true },
        ]
    : []

  const linhas: Array<{ rotulo: string; de: (p: PontoCalculadora) => number; nota?: string; forte?: boolean; ok?: boolean }> = [
    { rotulo: 'Preço, sem o imposto', de: (p) => p.preco, nota: 'o que você negocia' },
    { rotulo: 'IBS/CBS por fora', de: (p) => p.tributo, nota: 'cobrado do cliente na nota' },
    { rotulo: 'Valor da nota', de: (p) => p.nota, forte: true },
    {
      rotulo: simples ? 'DAS desta venda' : 'Tributos por dentro do preço',
      de: (p) => p.das,
      nota: simples ? 'já sem as parcelas de IBS/CBS' : 'hoje PIS/COFINS, ICMS, ISS e IPI; em 2027 só o ICMS/ISS que resta',
    },
    { rotulo: 'Crédito das compras', de: (p) => p.credito, ok: true },
    { rotulo: 'Fica no seu caixa', de: (p) => p.caixa, nota: simples ? 'preço − DAS + crédito' : 'preço − tributos por dentro + crédito', forte: true },
    { rotulo: 'Custo real do cliente', de: (p) => p.custoCliente, nota: 'a nota menos o que ele credita', forte: true },
  ]

  return (
    <div className="flex flex-col gap-5">
      <header>
        <h1 className="text-2xl font-semibold">Calculadora de preço</h1>
        <p className="mt-1 max-w-3xl text-sm text-tinta-2">
          Uma venda por vez, com os números reais da empresa.{' '}
          {simples
            ? 'O DAS sai da faixa dela; no híbrido, as parcelas de IBS/CBS saem do DAS e o imposto passa a ser destacado por fora, cobrado do cliente.'
            : 'Hoje a venda paga PIS/COFINS, ICMS, ISS e IPI por dentro do preço; em 2027 o PIS/COFINS vira CBS cobrada por fora, e o ICMS/ISS segue na transição.'}{' '}
          O crédito das compras ligadas àquela venda entra no caixa.
        </p>
      </header>

      {contexto && (
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 rounded-lg border border-linha bg-painel px-5 py-3 text-[13px]">
          <span className="text-tinta-3">A faixa desta empresa:</span>
          <span>
            receita de 12 meses <strong className="num">{reais(contexto.rbt12)}</strong>
          </span>
          {simples && contexto.anexoSimples && <span>Anexo {contexto.anexoSimples}</span>}
          {r && (
            <>
              {simples ? (
                <>
                  <span>
                    DAS de <strong className="num">{pctTexto(r.aliquotas.efetiva)}</strong> (faixa {r.aliquotas.faixa})
                  </span>
                  <span>
                    no híbrido o DAS cai para <strong className="num">{pctTexto(r.aliquotas.efetiva - r.aliquotas.efetivaCbsIbs)}</strong>
                  </span>
                </>
              ) : (
                <span>
                  tributos de hoje sobre a venda: <strong className="num">{pctTexto(r.aliquotas.efetiva)}</strong>
                </span>
              )}
              <span>
                e entra <strong className="num">{pctTexto(r.aliquotas.total)}</strong> de IBS/CBS por fora
              </span>
              {r.aliquotas.status === 'ESTIMATIVA' && <Selo tom="neutro">alíquota estimada</Selo>}
            </>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[13px] text-tinta-3">Regime da empresa:</span>
        {(
          [
            ['SIMPLES', 'Simples Nacional'],
            ['PRESUMIDO', 'Lucro Presumido'],
            ['REAL', 'Lucro Real'],
          ] as const
        ).map(([chave, rotulo]) => (
          <button
            key={chave}
            type="button"
            onClick={() => trocarRegime(chave)}
            aria-pressed={e.regimeEmpresa === chave}
            className={`rounded-md border px-3 py-1.5 text-[13px] font-medium ${e.regimeEmpresa === chave ? 'border-navy bg-navy text-white' : 'border-linha text-navy hover:border-navy'}`}
          >
            {rotulo}
          </button>
        ))}
      </div>

      <div className="flex gap-1.5">
        {(
          [
            ['CONVERTER', 'Já vendo por…'],
            ['FORMAR', 'Formar preço do zero'],
          ] as const
        ).map(([chave, rotulo]) => (
          <button
            key={chave}
            type="button"
            onClick={() => setModo(chave)}
            aria-pressed={modo === chave}
            className={`rounded-md border px-3 py-1.5 text-sm font-medium ${modo === chave ? 'border-navy bg-navy text-white' : 'border-linha text-navy hover:border-navy'}`}
          >
            {rotulo}
          </button>
        ))}
      </div>

      <section className="grid gap-4 rounded-lg border border-linha bg-painel p-5 sm:grid-cols-2 lg:grid-cols-4">
        {converter ? (
          <label className="flex min-w-0 flex-col gap-1 text-[13px]">
            <span className="font-medium">Valor da venda hoje</span>
            <EntradaNumero valor={e.valorVenda} onValor={(v) => campo('valorVenda', v ?? 0)} tipo="reais" aria-label="Valor da venda hoje" />
            <span className="text-[11px] text-tinta-3">o que você cobra pela operação</span>
          </label>
        ) : (
          <>
            <label className="flex min-w-0 flex-col gap-1 text-[13px]">
              <span className="font-medium">Custo da mercadoria ou do insumo</span>
              <EntradaNumero valor={e.custo} onValor={(v) => campo('custo', v ?? 0)} tipo="reais" aria-label="Custo" />
            </label>
            <label className="flex min-w-0 flex-col gap-1 text-[13px]">
              <span className="font-medium">Despesas sobre o preço</span>
              <EntradaNumero valor={e.despesas} onValor={(v) => campo('despesas', v ?? 0)} tipo="percentual" aria-label="Despesas" />
              <span className="text-[11px] text-tinta-3">comissão, frete, taxa de cartão</span>
            </label>
            <label className="flex min-w-0 flex-col gap-1 text-[13px]">
              <span className="font-medium">Margem desejada</span>
              <EntradaNumero valor={e.margem} onValor={(v) => campo('margem', v ?? 0)} tipo="percentual" aria-label="Margem desejada" />
              <span className="text-[11px] text-tinta-3">sobre o preço, depois do imposto</span>
            </label>
          </>
        )}

        {!simples && (
          <>
            <label className="flex min-w-0 flex-col gap-1 text-[13px]">
              <span className="font-medium">PIS/COFINS de hoje</span>
              <EntradaNumero valor={e.pisCofins} onValor={(v) => campo('pisCofins', v ?? 0)} tipo="percentual" aria-label="PIS/COFINS de hoje" />
              <span className="text-[11px] text-tinta-3">{e.regimeEmpresa === 'REAL' ? 'não cumulativo: credita as compras' : 'cumulativo: não credita'}</span>
            </label>
            <label className="flex min-w-0 flex-col gap-1 text-[13px]">
              <span className="font-medium">ICMS na venda</span>
              <EntradaNumero valor={e.icms} onValor={(v) => campo('icms', v ?? 0)} tipo="percentual" aria-label="ICMS na venda" />
              <span className="text-[11px] text-tinta-3">destacado na nota, hoje</span>
            </label>
            <label className="flex min-w-0 flex-col gap-1 text-[13px]">
              <span className="font-medium">ISS na venda</span>
              <EntradaNumero valor={e.iss} onValor={(v) => campo('iss', v ?? 0)} tipo="percentual" aria-label="ISS na venda" />
            </label>
            <label className="flex min-w-0 flex-col gap-1 text-[13px]">
              <span className="font-medium">IPI na venda</span>
              <EntradaNumero valor={e.ipi} onValor={(v) => campo('ipi', v ?? 0)} tipo="percentual" aria-label="IPI na venda" />
              <span className="text-[11px] text-tinta-3">acaba em 2027</span>
            </label>
            <label className="flex min-w-0 flex-col gap-1 text-[13px]">
              <span className="font-medium">ICMS na compra</span>
              <EntradaNumero valor={e.icmsCompra} onValor={(v) => campo('icmsCompra', v ?? 0)} tipo="percentual" aria-label="ICMS na compra" />
              <span className="text-[11px] text-tinta-3">o crédito que ela já dá hoje</span>
            </label>
          </>
        )}

        <label className="flex min-w-0 flex-col gap-1 text-[13px]">
          <span className="font-medium">Compras ligadas a esta venda</span>
          <EntradaNumero valor={e.compra} onValor={(v) => campo('compra', v ?? 0)} tipo="reais" aria-label="Compras ligadas a esta venda" />
          <span className="text-[11px] text-tinta-3">o que você comprou para entregar</span>
        </label>
        <label className="flex min-w-0 flex-col gap-1 text-[13px]">
          <span className="font-medium">Regime de quem lhe vendeu</span>
          <select
            value={e.regimeFornecedor}
            onChange={(ev) => campo('regimeFornecedor', ev.target.value as RegimeContraparte)}
            className="w-full min-w-0 rounded-md border border-linha bg-painel px-3 py-2 text-sm"
          >
            {REGIMES_UTEIS.map((x) => (
              <option key={x} value={x}>
                {ROTULO_REGIME_CONTRAPARTE[x]}
              </option>
            ))}
          </select>
          <span className="text-[11px] text-tinta-3">decide quanto da compra vira crédito</span>
        </label>
        <label className="flex min-w-0 flex-col gap-1 text-[13px]">
          <span className="font-medium">O que você vende</span>
          <select value={e.tratamento} onChange={(ev) => campo('tratamento', ev.target.value as EntradaCalculadora['tratamento'])} className="w-full min-w-0 rounded-md border border-linha bg-painel px-3 py-2 text-sm">
            {TRATAMENTOS.filter((t) => ROTULO_TRATAMENTO[t]).map((t) => (
              <option key={t} value={t}>
                {ROTULO_TRATAMENTO[t]}
              </option>
            ))}
          </select>
        </label>
        <div className="flex min-w-0 flex-col gap-2 text-[13px]">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={e.clienteCredita} onChange={(ev) => campo('clienteCredita', ev.target.checked)} className="size-4 accent-navy" />
            <span className="font-medium">O cliente aproveita o crédito</span>
          </label>
          <span className="text-[11px] text-tinta-3">empresa do regime regular sim; consumidor final, MEI e Simples puro, não</span>
          {!simples && (
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={e.clienteCreditaPisCofins}
                onChange={(ev) => campo('clienteCreditaPisCofins', ev.target.checked)}
                className="size-4 accent-navy"
                disabled={!e.clienteCredita}
              />
              <span>O cliente é do Lucro Real</span>
            </label>
          )}
          {!simples && <span className="text-[11px] text-tinta-3">só ele credita o PIS/COFINS da sua nota hoje</span>}
          <label className="flex items-center gap-2">
            <span>Ano</span>
            <select value={e.ano} onChange={(ev) => campo('ano', Number(ev.target.value))} className="rounded-md border border-linha bg-painel px-2 py-1 text-sm">
              {[2027, 2028, 2029, 2030, 2031, 2032, 2033].map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </label>
        </div>
      </section>

      {erro ? <Aviso>{mensagemDeErro(erro)}</Aviso> : null}
      {r && (
        <>
          <section className="flex flex-col gap-3 rounded-lg border border-linha bg-painel p-5">
            <p className="text-[11px] text-tinta-3 lg:hidden">Arraste a tabela para o lado para ver todas as colunas.</p>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[46rem] text-[13px]">
                <thead>
                  <tr className="border-b border-linha text-left text-[11px] text-tinta-3">
                    <th className="py-2 font-medium" />
                    {colunas.map((c) => (
                      <th key={c.titulo} className={`py-2 pl-4 text-right font-medium ${c.destaque ? 'text-navy' : ''}`}>
                        <span className="block text-[13px] font-semibold">{c.titulo}</span>
                        <span className="block font-normal">{c.nota}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {linhas.map((l) => (
                    <tr key={l.rotulo} className="border-b border-linha last:border-0">
                      <td className={`py-1.5 ${l.forte ? 'font-medium' : ''}`}>
                        {l.rotulo}
                        {l.nota ? <span className="block text-[11px] text-tinta-3">{l.nota}</span> : null}
                      </td>
                      {colunas.map((c) => (
                        <td
                          key={c.titulo}
                          className={`num whitespace-nowrap py-1.5 pl-4 text-right ${l.forte ? 'font-semibold' : ''} ${l.ok && l.de(c.p) > 0 ? 'text-ok' : ''} ${c.destaque ? 'bg-fundo' : ''}`}
                        >
                          {reais(l.de(c.p))}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {r.demonstracao && (
            <section className="flex flex-col gap-4 rounded-lg border border-linha bg-painel p-5">
              <div>
                <h2 className="text-base font-semibold">A demonstração para o cliente</h2>
                <p className="mt-0.5 max-w-3xl text-[13px] text-tinta-2">
                  Fixando o valor do produto no que ele já paga de verdade hoje: a nota cresce, ele credita o imposto inteiro, e o custo dele não muda nem um centavo.
                </p>
              </div>
              <div className="grid gap-4 lg:grid-cols-2">
                {(
                  [
                    ['Simples como está', r.demonstracao.puro.linhas, false],
                    ['Simples híbrido', r.demonstracao.hibrido.linhas, true],
                  ] as const
                ).map(([titulo, linhas, destaque]) => (
                  <div key={titulo} className={`flex flex-col gap-1 rounded-md p-4 ${destaque ? 'bg-navy text-white' : 'bg-fundo'}`}>
                    <h3 className={`text-[13px] font-semibold ${destaque ? '' : 'text-navy'}`}>{titulo}</h3>
                    {linhas.map((l, i) => (
                      <div
                        key={`${l.rotulo}-${i}`}
                        className={`flex items-baseline justify-between gap-3 py-1 text-[13px] ${l.sinal === '=' ? `border-t ${destaque ? 'border-white/25' : 'border-linha'} font-semibold` : ''}`}
                      >
                        <span className="flex gap-1.5">
                          <span className={`w-3 shrink-0 ${destaque ? 'text-white/60' : 'text-tinta-3'}`}>{l.sinal}</span>
                          <span>
                            {l.rotulo}
                            {l.nota ? <span className={`block text-[11px] ${destaque ? 'text-white/70' : 'text-tinta-3'}`}>{l.nota}</span> : null}
                          </span>
                        </span>
                        <span className="num whitespace-nowrap">{reais(l.valor)}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>

              <div className="grid gap-3 sm:grid-cols-3">
                <div className="rounded-md bg-fundo p-3">
                  <p className="text-[11px] text-tinta-3">A nota precisa subir</p>
                  <p className="num text-xl font-semibold">+{pctTexto(r.demonstracao.aumentoNota)}</p>
                  <p className="text-[11px] text-tinta-3">
                    de {reais(r.demonstracao.puro.nota)} para {reais(r.demonstracao.hibrido.nota)}
                  </p>
                </div>
                <div className="rounded-md bg-fundo p-3">
                  <p className="text-[11px] text-tinta-3">Crédito que ele passa a ter</p>
                  <p className="num text-xl font-semibold text-ok">{reais(r.demonstracao.hibrido.credito)}</p>
                  <p className="text-[11px] text-tinta-3">
                    {reais(r.demonstracao.creditoAMais)} a mais do que os {reais(r.demonstracao.puro.credito)} de hoje
                  </p>
                </div>
                <div className="rounded-md bg-fundo p-3">
                  <p className="text-[11px] text-tinta-3">Cada 1% a mais na nota</p>
                  <p className="num text-xl font-semibold">{reais(r.demonstracao.creditoPorPontoPercentual)}</p>
                  <p className="text-[11px] text-tinta-3">de crédito cheio para ele</p>
                </div>
              </div>

              <div className="flex flex-wrap items-end gap-4 rounded-md border-l-4 border-navy bg-fundo p-4">
                <label className="flex min-w-0 flex-col gap-1 text-[13px]">
                  <span className="font-medium">Quero gerar este crédito para ele</span>
                  <EntradaNumero valor={e.creditoDesejado} onValor={(v) => campo('creditoDesejado', v ?? 0)} tipo="reais" aria-label="Crédito que quero gerar" />
                </label>
                <p className="flex-1 text-[13px] text-tinta-2">
                  {r.demonstracao.paraCreditoDesejado ? (
                    <>
                      Para entregar <strong className="num">{reais(r.demonstracao.paraCreditoDesejado.desejado)}</strong> de crédito, o valor do produto precisa ser{' '}
                      <strong className="num">{reais(r.demonstracao.paraCreditoDesejado.preco)}</strong> e a nota{' '}
                      <strong className="num">{reais(r.demonstracao.paraCreditoDesejado.nota)}</strong> — um aumento de{' '}
                      <strong className="num">
                        {r.demonstracao.paraCreditoDesejado.aumentoNota >= 0 ? '+' : '−'}
                        {pctTexto(r.demonstracao.paraCreditoDesejado.aumentoNota)}
                      </strong>{' '}
                      sobre a nota de hoje.
                    </>
                  ) : (
                    'Informe um valor para ver de quanto a nota precisa subir para gerar esse crédito.'
                  )}
                </p>
              </div>
            </section>
          )}

          {r.competitividade && (
            <section className="flex flex-col gap-4 rounded-lg border border-linha bg-painel p-5">
              <div>
                <h2 className="text-base font-semibold">O crédito das suas compras vira competitividade</h2>
                <p className="mt-0.5 max-w-3xl text-[13px] text-tinta-2">
                  No híbrido você passa a creditar o IBS/CBS do que compra. Esse dinheiro é seu: dá para ficar com ele ou devolvê-lo no preço e chegar mais barato que o concorrente
                  que não optou.
                </p>
              </div>

              <div className="grid gap-3 sm:grid-cols-3">
                <div className="rounded-md bg-fundo p-3">
                  <p className="text-[11px] text-tinta-3">Crédito desta venda</p>
                  <p className="num text-xl font-semibold text-ok">{reais(r.competitividade.credito)}</p>
                  <p className="text-[11px] text-tinta-3">{r.competitividade.motivo}</p>
                </div>
                <div className="rounded-md bg-fundo p-3">
                  <p className="text-[11px] text-tinta-3">Dá para tirar do preço</p>
                  <p className="num text-xl font-semibold">{reais(r.competitividade.abatimentoPreco)}</p>
                  <p className="text-[11px] text-tinta-3">
                    {pctTexto(r.competitividade.abatimentoPpm)} do preço, sem tocar no seu caixa
                  </p>
                </div>
                <div className="rounded-md bg-navy p-3 text-white">
                  <p className="text-[11px] text-white/75">Cada R$ 1,00 de crédito</p>
                  <p className="num text-xl font-semibold">{reais(Math.round(r.competitividade.alavanca / 10_000))}</p>
                  <p className="text-[11px] text-white/75">de desconto no preço — o DAS cai junto</p>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full min-w-[34rem] text-[13px]">
                  <thead>
                    <tr className="border-b border-linha text-left text-[11px] text-tinta-3">
                      <th className="py-2 font-medium" />
                      <th className="py-2 pl-4 text-right font-medium">
                        <span className="block text-[13px] font-semibold">Ficando com o crédito</span>
                        <span className="block font-normal">preço igual, caixa maior</span>
                      </th>
                      <th className="py-2 pl-4 text-right font-medium text-navy">
                        <span className="block text-[13px] font-semibold">Devolvendo no preço</span>
                        <span className="block font-normal">caixa de hoje, cliente mais barato</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(
                      [
                        ['Preço, sem o imposto', (p: PontoCalculadora) => p.preco, false],
                        ['Valor da nota', (p: PontoCalculadora) => p.nota, true],
                        ['Custo real do cliente', (p: PontoCalculadora) => p.custoCliente, true],
                        ['Fica no seu caixa', (p: PontoCalculadora) => p.caixa, true],
                      ] as Array<[string, (p: PontoCalculadora) => number, boolean]>
                    ).map(([rotulo, de, forte]) => (
                      <tr key={rotulo} className="border-b border-linha last:border-0">
                        <td className={`py-1.5 ${forte ? 'font-medium' : ''}`}>{rotulo}</td>
                        <td className={`num whitespace-nowrap py-1.5 pl-4 text-right ${forte ? 'font-semibold' : ''}`}>{reais(de(r.competitividade!.semUsar))}</td>
                        <td className={`num whitespace-nowrap bg-fundo py-1.5 pl-4 text-right ${forte ? 'font-semibold' : ''}`}>{reais(de(r.competitividade!.usando))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <p className="rounded-md border-l-4 border-navy bg-fundo px-3 py-2 text-[12px] text-tinta-2">
                Devolvendo o crédito no preço, o cliente paga <strong className="num">{reais(r.competitividade.economiaCliente)}</strong> a menos (
                {pctTexto(r.competitividade.economiaClientePpm)}) e o seu caixa continua o mesmo de hoje. Repare que o desconto (
                <strong className="num">{reais(r.competitividade.abatimentoPreco)}</strong>) é maior que o crédito (
                <strong className="num">{reais(r.competitividade.credito)}</strong>): ao baixar o preço, o DAS daquela venda cai junto.
              </p>
            </section>
          )}

          {!simples && (
            <section className="flex flex-col gap-2 rounded-lg border border-linha bg-painel p-5">
              <h2 className="text-sm font-semibold">O que esta venda paga hoje</h2>
              <div className="flex flex-wrap gap-2">
                {(
                  [
                    ['PIS/COFINS', r.cargaHoje.pisCofins, e.regimeEmpresa === 'REAL' ? 'não cumulativo' : 'cumulativo'],
                    ['ICMS', r.cargaHoje.icms, 'destacado na nota'],
                    ['ISS', r.cargaHoje.iss, 'serviço'],
                    ['IPI', r.cargaHoje.ipi, 'acaba em 2027'],
                  ] as Array<[string, number, string]>
                )
                  .filter(([, v]) => v > 0)
                  .map(([rotulo, valor, nota]) => (
                    <div key={rotulo} className="min-w-[9rem] flex-1 rounded-md bg-fundo p-3">
                      <p className="text-[11px] text-tinta-3">{rotulo}</p>
                      <p className="num text-base font-semibold">{reais(valor)}</p>
                      <p className="text-[11px] text-tinta-3">{nota}</p>
                    </div>
                  ))}
                {r.cargaHoje.creditos > 0 && (
                  <div className="min-w-[9rem] flex-1 rounded-md bg-fundo p-3">
                    <p className="text-[11px] text-tinta-3">Créditos das compras</p>
                    <p className="num text-base font-semibold text-ok">− {reais(r.cargaHoje.creditos)}</p>
                    <p className="text-[11px] text-tinta-3">{e.regimeEmpresa === 'REAL' ? 'ICMS e PIS/COFINS' : 'só o ICMS'}</p>
                  </div>
                )}
                <div className="min-w-[9rem] flex-1 rounded-md bg-navy p-3 text-white">
                  <p className="text-[11px] text-white/75">Carga líquida de hoje</p>
                  <p className="num text-base font-semibold">{reais(r.cargaHoje.total)}</p>
                  <p className="text-[11px] text-white/75">{pctTexto(r.aliquotas.efetiva)} sobre a venda, antes do crédito</p>
                </div>
              </div>
            </section>
          )}

          <section className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-md border border-linha bg-painel p-4">
              <p className="text-[11px] text-tinta-3">{converter ? 'A nota sobe' : 'Preço formado contra hoje'}</p>
              <p className="num text-2xl font-semibold">
                {r.piso.variacaoNota >= 0 ? '+' : '−'}
                {pctTexto(r.piso.variacaoNota)}
              </p>
              <p className="text-[11px] text-tinta-3">
                de {reais(r.hoje.nota)} para {reais(r.piso.nota)}
              </p>
            </div>
            <div className="rounded-md border border-linha bg-painel p-4">
              <p className="text-[11px] text-tinta-3">O custo do cliente</p>
              <p className={`num text-2xl font-semibold ${r.piso.variacaoCustoCliente <= 0 ? 'text-ok' : 'text-alerta'}`}>
                {r.piso.variacaoCustoCliente >= 0 ? '+' : '−'}
                {pctTexto(r.piso.variacaoCustoCliente)}
              </p>
              <p className="text-[11px] text-tinta-3">
                {reais(r.hoje.custoCliente)} → {reais(r.piso.custoCliente)}
              </p>
            </div>
            <div className="rounded-md bg-navy p-4 text-white">
              <p className="text-[11px] text-white/75">No seu caixa</p>
              <p className="num text-2xl font-semibold">
                {r.piso.caixa - r.hoje.caixa >= 0 ? '+' : '−'}
                {reais(Math.abs(r.piso.caixa - r.hoje.caixa))}
              </p>
              <p className="text-[11px] text-white/75">
                {reais(r.hoje.caixa)} → {reais(r.piso.caixa)}
              </p>
            </div>
          </section>

          {!converter && r.piso.caixa < r.hoje.caixa && (
            <p className="rounded-md border-l-4 border-navy bg-fundo px-3 py-2 text-[12px] text-tinta-2">
              A margem de <strong>{pctTexto(e.margem)}</strong> é a mesma nos dois regimes — mas ela é um percentual do preço, e o preço cai quando o imposto sai de dentro dele.
              Por isso o caixa em reais fica {reais(r.hoje.caixa - r.piso.caixa)} menor. Para manter o valor em reais, use a aba <strong>Já vendo por…</strong> e o preço do piso: lá
              a conta parte do caixa de hoje, não da margem percentual.
            </p>
          )}

          <section className="flex flex-col gap-2 rounded-lg border-l-4 border-navy bg-fundo p-5">
            <h2 className="text-sm font-semibold">Para os próximos orçamentos</h2>
            <p className="text-[13px] text-tinta-2">
              Multiplique o preço que você praticaria hoje por <strong className="num">{(r.fatorPreco / 1_000_000).toFixed(4).replace('.', ',')}</strong> e destaque o IBS/CBS por
              fora. O valor final da nota fica <strong className="num">{(r.fatorNota / 1_000_000).toFixed(4).replace('.', ',')}×</strong> o de hoje
              {r.piso.variacaoCustoCliente < 0 ? ', e ainda assim o cliente que se credita paga menos do que paga hoje.' : '.'}
            </p>
            <p className="text-[12px] text-tinta-3">
              Exemplo: uma venda de {reais(100_000)} de hoje passa a {reais(Math.round((100_000 * r.fatorPreco) / 1_000_000))} + imposto ={' '}
              {reais(Math.round((100_000 * r.fatorNota) / 1_000_000))} na nota.
            </p>
          </section>

          {r.credito.valor > 0 && (
            <p className="text-[12px] text-tinta-3">
              Crédito considerado: {reais(r.credito.valor)} — {r.credito.motivo}.
            </p>
          )}
          {r.avisos.length > 0 && (
            <ul className="list-disc space-y-1 pl-5 text-[12px] text-tinta-3">
              {r.avisos.map((a) => (
                <li key={a}>{a}</li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  )
}
